import { useMemo, useState } from "react"
import dayjs from "lib/dayjs"

import { DATE_FORMATS } from "constants/dateFormats"

import { SimpleLineChart } from "components/common/SimpleLineChart"

import {
  ArrowIcon,
  ChartFade,
  ChartOverlay,
  Column,
  ContentWrapper,
  DaysText,
  Label,
  TooltipBox,
  TooltipLine,
  TooltipTitle,
  TrendBadge,
  TrendPercent,
  TrendRow,
  ValueContainer,
  ValueExtra,
  ValueRow,
  ValueText,
} from "./styled"

const formatTooltipDate = (date: Date): string => dayjs(date).format(DATE_FORMATS.LOCALIZED_DATE)

function getPeriodDateRange(period: number, offset: number): string {
  const end = new Date()
  end.setDate(end.getDate() - 1 - offset * period)
  const start = new Date()
  start.setDate(start.getDate() - period - offset * period)
  return `${formatTooltipDate(start)} - ${formatTooltipDate(end)}`
}

function getLastNDaysLabels(n: number): string[] {
  const labels: string[] = []
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date()
    d.setDate(d.getDate() - i)
    labels.push(d.toLocaleDateString("en-US", { month: "short", day: "numeric" }))
  }
  return labels
}

const FlatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M4 8h12M4 12h12" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

export interface TrendTooltipPeriod {
  period: number
  lastPeriodCount: number | string
  prevPeriodCount: number | string
  unit: string
}

export interface TrendColumnData {
  label: string
  value: string | number
  valueExtra?: string
  tooltip: string
  tooltipPeriod?: TrendTooltipPeriod
  trend: {
    direction: "up" | "down" | "flat"
    percent?: string
    days: string
  }
  chart?: { data: number[]; color: string; labels?: string[] } | null
}

interface TrendColumnProps extends TrendColumnData {
  hasBorder?: boolean
}

export const TrendColumn = ({
  label,
  value,
  valueExtra,
  tooltip,
  tooltipPeriod,
  trend,
  chart,
  hasBorder,
}: TrendColumnProps) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const chartLabels = useMemo(
    () => (chart ? (chart.labels ?? getLastNDaysLabels(chart.data.length)) : []),
    [chart?.data.length, chart?.labels],
  )

  return (
    <Column $hasBorder={hasBorder}>
      {chart && (
        <ChartOverlay>
          <SimpleLineChart data={chart.data} color={chart.color} labels={chartLabels} />
          <ChartFade />
        </ChartOverlay>
      )}
      <ContentWrapper onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
        <Label>{label}</Label>
        <ValueContainer>
          <ValueRow>
            <ValueText>{value}</ValueText>
            {valueExtra && <ValueExtra>{valueExtra}</ValueExtra>}
            <TooltipBox $visible={showTooltip}>
              {tooltipPeriod ? (
                <>
                  <TooltipTitle>{label}</TooltipTitle>
                  <TooltipLine>
                    Last {tooltipPeriod.period} days ({getPeriodDateRange(tooltipPeriod.period, 0)}) -{" "}
                    {tooltipPeriod.lastPeriodCount} {tooltipPeriod.unit}
                  </TooltipLine>
                  <TooltipLine>
                    Past {tooltipPeriod.period} days ({getPeriodDateRange(tooltipPeriod.period, 1)}) -{" "}
                    {tooltipPeriod.prevPeriodCount} {tooltipPeriod.unit}
                  </TooltipLine>
                </>
              ) : (
                tooltip
              )}
            </TooltipBox>
          </ValueRow>
        </ValueContainer>
        <TrendRow>
          {trend.direction !== "flat" ? (
            <>
              <TrendBadge>
                <ArrowIcon $direction={trend.direction as "up" | "down"} />
                <TrendPercent $direction={trend.direction}>{trend.percent}</TrendPercent>
              </TrendBadge>
              <DaysText>{trend.days}</DaysText>
            </>
          ) : (
            <>
              <FlatIcon />
              <DaysText>{trend.days}</DaysText>
            </>
          )}
        </TrendRow>
      </ContentWrapper>
    </Column>
  )
}
